import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const UTM_STORAGE_KEY = 'zs_utm';
const PAGE_VIEWS_KEY = 'zs_page_views';
const LANDING_KEY = 'zs_landing';
const REFERRER_KEY = 'zs_referrer';

interface Attribution {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  referrer: string | null;
  landing_page: string | null;
  device_type: string;
  session_page_views: number;
}

function detectDeviceType(): string {
  const ua = navigator.userAgent;
  if (/Mobi|Android/i.test(ua)) return 'mobile';
  if (/Tablet|iPad/i.test(ua)) return 'tablet';
  return 'desktop';
}

function getOriginLabel(source: string | null, referrer: string | null): string {
  if (source) return source;
  if (!referrer) return 'direct';
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    if (host.includes('google')) return 'google';
    if (host.includes('bing')) return 'bing';
    if (host.includes('instagram')) return 'instagram';
    if (host.includes('facebook') || host.includes('fb.')) return 'facebook';
    if (host.includes('youtube')) return 'youtube';
    if (host.includes('twitter') || host.includes('x.com')) return 'twitter';
    if (host.includes('reddit')) return 'reddit';
    if (host.includes('whatsapp')) return 'whatsapp';
    return host;
  } catch {
    return 'unknown';
  }
}

/** Capture UTM params on first visit and track page views per session */
export function useAttributionCapture() {
  const initialized = useRef(false);
  const location = useLocation();

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');
    const utmTerm = params.get('utm_term');
    const utmContent = params.get('utm_content');

    // Store UTM params if present (first-touch — don't overwrite existing)
    if (utmSource && !sessionStorage.getItem(UTM_STORAGE_KEY)) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify({
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        utm_term: utmTerm,
        utm_content: utmContent,
      }));
    }

    // Store landing page (first page of session)
    if (!sessionStorage.getItem(LANDING_KEY)) {
      sessionStorage.setItem(LANDING_KEY, window.location.pathname + window.location.search);
    }

    // Store referrer (first touch)
    if (!sessionStorage.getItem(REFERRER_KEY) && document.referrer) {
      // Only store external referrers
      try {
        const refHost = new URL(document.referrer).hostname;
        if (refHost !== window.location.hostname) {
          sessionStorage.setItem(REFERRER_KEY, document.referrer);
        }
      } catch { /* ignore */ }
    }
  }, []);

  // Count one page view per rendered route (landing page = 1, +1 per navigation)
  useEffect(() => {
    const views = parseInt(sessionStorage.getItem(PAGE_VIEWS_KEY) || '0', 10);
    sessionStorage.setItem(PAGE_VIEWS_KEY, String(views + 1));
  }, [location.pathname]);
}

/** Get attribution data to include in order placement */
export function getAttribution(): Attribution {
  const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
  let utm: Record<string, string> = {};
  if (stored) { try { utm = JSON.parse(stored); } catch { /* corrupted — ignore */ } }
  const referrer = sessionStorage.getItem(REFERRER_KEY) || null;
  const utmSource = utm.utm_source || getOriginLabel(null, referrer);

  return {
    utm_source: utmSource || null,
    utm_medium: utm.utm_medium || null,
    utm_campaign: utm.utm_campaign || null,
    utm_term: utm.utm_term || null,
    utm_content: utm.utm_content || null,
    referrer,
    landing_page: sessionStorage.getItem(LANDING_KEY) || null,
    device_type: detectDeviceType(),
    session_page_views: parseInt(sessionStorage.getItem(PAGE_VIEWS_KEY) || '1', 10),
  };
}
